/**
 * Mock Fixtures
 * Realistic XMPP-compliant seed data for development
 */
import { buildJid, generateMessageId } from './helpers';
// ============================================================================
// Configuration
// ============================================================================
export const MOCK_DOMAIN = 'wargame.local';
export const MOCK_CONFERENCE = `conference.${MOCK_DOMAIN}`;
export const MOCK_PUBSUB = `pubsub.${MOCK_DOMAIN}`;
// ============================================================================
// Users - Using UserInfo (no longer XMPP rosters)
// ============================================================================
export const MOCK_USERS = [
    {
        jid: buildJid('commander.red', MOCK_DOMAIN),
        displayName: 'Red Force Commander',
        groups: ['force-red', 'Commanders'],
    },
    {
        jid: buildJid('commander.blue', MOCK_DOMAIN),
        displayName: 'Blue Force Commander',
        groups: ['force-blue', 'Commanders'],
    },
    {
        jid: buildJid('analyst.red1', MOCK_DOMAIN),
        displayName: 'Red Analyst 1',
        groups: ['force-red', 'Analysts'],
    },
    {
        jid: buildJid('analyst.blue1', MOCK_DOMAIN),
        displayName: 'Blue Analyst 1',
        groups: ['force-blue', 'Analysts'],
    },
    {
        jid: buildJid('gamemaster', MOCK_DOMAIN),
        displayName: 'Game Master',
        groups: ['Control', 'Game Masters'],
    },
];
// ============================================================================
// Forces
// ============================================================================
export const MOCK_FORCES = [
    {
        id: 'force-red',
        name: 'Red Force',
        description: 'Opposing force conducting offensive operations',
        color: '#D32F2F',
        members: [buildJid('commander.red', MOCK_DOMAIN), buildJid('analyst.red1', MOCK_DOMAIN)],
        commander: buildJid('commander.red', MOCK_DOMAIN),
        metadata: {
            designation: 'OPFOR',
            strength: '2 members',
        },
        createdAt: '2025-01-15T08:00:00.000Z',
        createdBy: buildJid('gamemaster', MOCK_DOMAIN),
    },
    {
        id: 'force-blue',
        name: 'Blue Force',
        description: 'Friendly force conducting defensive operations',
        color: '#1976D2',
        members: [buildJid('commander.blue', MOCK_DOMAIN), buildJid('analyst.blue1', MOCK_DOMAIN)],
        commander: buildJid('commander.blue', MOCK_DOMAIN),
        metadata: {
            designation: 'BLUFOR',
            strength: '2 members',
        },
        createdAt: '2025-01-15T08:00:00.000Z',
        createdBy: buildJid('gamemaster', MOCK_DOMAIN),
    },
];
// ============================================================================
// Game Metadata
// ============================================================================
export const MOCK_GAME = {
    id: 'game-2025-winter-exercise',
    title: 'Winter Exercise 2025',
    description: 'Multi-domain wargaming exercise focusing on defensive operations',
    startTime: '2025-01-20T09:00:00.000Z',
    endTime: '2025-01-22T17:00:00.000Z',
    status: 'active',
    gameMasters: [buildJid('gamemaster', MOCK_DOMAIN)],
    metadata: {
        scenario: 'Defensive Operations',
        classification: 'UNCLASSIFIED',
        participants: 5,
    },
    createdAt: '2025-01-15T08:00:00.000Z',
    createdBy: buildJid('gamemaster', MOCK_DOMAIN),
    updatedAt: '2025-01-20T09:00:00.000Z',
};
// ============================================================================
// Game Theme (Material UI)
// ============================================================================
export const MOCK_GAME_THEME = {
    palette: {
        mode: 'dark',
        primary: {
            main: '#90CAF9',
        },
        secondary: {
            main: '#F48FB1',
        },
        background: {
            default: '#1A1A1A',
            paper: '#242424',
        },
    },
    typography: {
        fontFamily: '"Roboto Mono", "Courier New", monospace',
    },
};
// ============================================================================
// Rooms
// ============================================================================
export const MOCK_ROOMS = [
    {
        jid: buildJid('all-hands', MOCK_CONFERENCE),
        info: {
            type: 'info',
            identities: [
                {
                    category: 'conference',
                    type: 'text',
                    name: 'All Hands',
                },
            ],
            features: ['http://jabber.org/protocol/muc', 'muc_public', 'muc_persistent', 'muc_open'],
        },
        extension: {
            roomJid: buildJid('all-hands', MOCK_CONFERENCE),
            type: 'all-hands',
            formSchemaIds: ['sitrep', 'intsum'],
            createdAt: '2025-01-15T08:00:00.000Z',
            createdBy: buildJid('gamemaster', MOCK_DOMAIN),
        },
    },
    {
        jid: buildJid('red-command', MOCK_CONFERENCE),
        info: {
            type: 'info',
            identities: [
                {
                    category: 'conference',
                    type: 'text',
                    name: 'Red Force Command',
                },
            ],
            features: ['http://jabber.org/protocol/muc', 'muc_persistent', 'muc_membersonly'],
        },
        extension: {
            roomJid: buildJid('red-command', MOCK_CONFERENCE),
            type: 'command',
            forceRestrictions: ['force-red'],
            formSchemaIds: ['sitrep', 'oporder'],
            theme: {
                palette: {
                    primary: {
                        main: '#D32F2F',
                    },
                },
            },
            createdAt: '2025-01-15T08:00:00.000Z',
            createdBy: buildJid('gamemaster', MOCK_DOMAIN),
        },
    },
    {
        jid: buildJid('blue-command', MOCK_CONFERENCE),
        info: {
            type: 'info',
            identities: [
                {
                    category: 'conference',
                    type: 'text',
                    name: 'Blue Force Command',
                },
            ],
            features: ['http://jabber.org/protocol/muc', 'muc_persistent', 'muc_membersonly'],
        },
        extension: {
            roomJid: buildJid('blue-command', MOCK_CONFERENCE),
            type: 'command',
            forceRestrictions: ['force-blue'],
            formSchemaIds: ['sitrep', 'oporder'],
            theme: {
                palette: {
                    primary: {
                        main: '#1976D2',
                    },
                },
            },
            createdAt: '2025-01-15T08:00:00.000Z',
            createdBy: buildJid('gamemaster', MOCK_DOMAIN),
        },
    },
    {
        jid: buildJid('red-media', MOCK_CONFERENCE),
        info: {
            type: 'info',
            identities: [
                {
                    category: 'conference',
                    type: 'text',
                    name: 'Red Force Media',
                },
            ],
            features: ['http://jabber.org/protocol/muc', 'muc_persistent', 'muc_membersonly'],
        },
        extension: {
            roomJid: buildJid('red-media', MOCK_CONFERENCE),
            type: 'standard',
            forceRestrictions: ['force-red'],
            formSchemaIds: ['sitrep'],
            theme: {
                palette: {
                    primary: {
                        main: '#D32F2F',
                    },
                },
            },
            createdAt: '2025-01-15T08:00:00.000Z',
            createdBy: buildJid('gamemaster', MOCK_DOMAIN),
        },
    },
    {
        jid: buildJid('red-logistics', MOCK_CONFERENCE),
        info: {
            type: 'info',
            identities: [
                {
                    category: 'conference',
                    type: 'text',
                    name: 'Red Force Logistics',
                },
            ],
            features: ['http://jabber.org/protocol/muc', 'muc_persistent', 'muc_membersonly'],
        },
        extension: {
            roomJid: buildJid('red-logistics', MOCK_CONFERENCE),
            type: 'standard',
            forceRestrictions: ['force-red'],
            formSchemaIds: ['sitrep'],
            theme: {
                palette: {
                    primary: {
                        main: '#D32F2F',
                    },
                },
            },
            createdAt: '2025-01-15T08:00:00.000Z',
            createdBy: buildJid('gamemaster', MOCK_DOMAIN),
        },
    },
    {
        jid: buildJid('red-HQ', MOCK_CONFERENCE),
        info: {
            type: 'info',
            identities: [
                {
                    category: 'conference',
                    type: 'text',
                    name: 'Red Force HQ',
                },
            ],
            features: ['http://jabber.org/protocol/muc', 'muc_persistent', 'muc_membersonly'],
        },
        extension: {
            roomJid: buildJid('red-HQ', MOCK_CONFERENCE),
            type: 'standard',
            forceRestrictions: ['force-red'],
            formSchemaIds: ['sitrep', 'oporder'],
            theme: {
                palette: {
                    primary: {
                        main: '#D32F2F',
                    },
                },
            },
            createdAt: '2025-01-15T08:00:00.000Z',
            createdBy: buildJid('gamemaster', MOCK_DOMAIN),
        },
    },
];
// ============================================================================
// Sample Messages
// ============================================================================
export const MOCK_MESSAGES = [
    // All-Hands messages
    {
        id: generateMessageId(),
        from: `${buildJid('all-hands', MOCK_CONFERENCE)}/GM`,
        to: buildJid('all-hands', MOCK_CONFERENCE),
        type: 'groupchat',
        body: 'Welcome to Winter Exercise 2025. Exercise start time is 0900Z.',
        delay: {
            timestamp: new Date('2025-01-20T08:55:00.000Z'),
        },
    },
    {
        id: generateMessageId(),
        from: `${buildJid('all-hands', MOCK_CONFERENCE)}/RedCmd`,
        to: buildJid('all-hands', MOCK_CONFERENCE),
        type: 'groupchat',
        body: 'Red Force standing by.',
        delay: {
            timestamp: new Date('2025-01-20T08:56:00.000Z'),
        },
    },
    {
        id: generateMessageId(),
        from: `${buildJid('all-hands', MOCK_CONFERENCE)}/BlueCmd`,
        to: buildJid('all-hands', MOCK_CONFERENCE),
        type: 'groupchat',
        body: 'Blue Force ready.',
        delay: {
            timestamp: new Date('2025-01-20T08:57:00.000Z'),
        },
    },
    {
        id: generateMessageId(),
        from: `${buildJid('all-hands', MOCK_CONFERENCE)}/GM`,
        to: buildJid('all-hands', MOCK_CONFERENCE),
        type: 'groupchat',
        body: 'Exercise is now LIVE. All forces proceed with initial objectives.',
        delay: {
            timestamp: new Date('2025-01-20T09:00:00.000Z'),
        },
    },
    {
        id: generateMessageId(),
        from: `${buildJid('all-hands', MOCK_CONFERENCE)}/RedA1`,
        to: buildJid('all-hands', MOCK_CONFERENCE),
        type: 'groupchat',
        body: 'Roger that. Beginning reconnaissance operations.',
        delay: {
            timestamp: new Date('2025-01-20T09:01:00.000Z'),
        },
    },
    {
        id: generateMessageId(),
        from: `${buildJid('all-hands', MOCK_CONFERENCE)}/BlueA1`,
        to: buildJid('all-hands', MOCK_CONFERENCE),
        type: 'groupchat',
        body: 'Blue analyst standing by for intelligence updates.',
        delay: {
            timestamp: new Date('2025-01-20T09:02:00.000Z'),
        },
    },
    // Red Command messages
    {
        id: generateMessageId(),
        from: `${buildJid('red-command', MOCK_CONFERENCE)}/RedCmd`,
        to: buildJid('red-command', MOCK_CONFERENCE),
        type: 'groupchat',
        body: 'RedA1, prepare initial SITREP for T+30 minutes.',
        delay: {
            timestamp: new Date('2025-01-20T09:05:00.000Z'),
        },
    },
    {
        id: generateMessageId(),
        from: `${buildJid('red-command', MOCK_CONFERENCE)}/RedA1`,
        to: buildJid('red-command', MOCK_CONFERENCE),
        type: 'groupchat',
        body: 'Understood, Commander. Working on initial assessment now.',
        delay: {
            timestamp: new Date('2025-01-20T09:06:00.000Z'),
        },
    },
    {
        id: generateMessageId(),
        from: `${buildJid('red-command', MOCK_CONFERENCE)}/RedCmd`,
        to: buildJid('red-command', MOCK_CONFERENCE),
        type: 'groupchat',
        body: 'Priority is to establish situational awareness before H+1.',
        delay: {
            timestamp: new Date('2025-01-20T09:10:00.000Z'),
        },
    },
    {
        id: generateMessageId(),
        from: `${buildJid('red-command', MOCK_CONFERENCE)}/RedA1`,
        to: buildJid('red-command', MOCK_CONFERENCE),
        type: 'groupchat',
        body: 'SITREP: All units in position. No hostile contact yet.',
        delay: {
            timestamp: new Date('2025-01-20T09:30:00.000Z'),
        },
    },
    // Red Media messages
    {
        id: generateMessageId(),
        from: `${buildJid('red-media', MOCK_CONFERENCE)}/RedCmd`,
        to: buildJid('red-media', MOCK_CONFERENCE),
        type: 'groupchat',
        body: 'Media team, prepare for initial press release at H+2.',
        delay: {
            timestamp: new Date('2025-01-20T09:15:00.000Z'),
        },
    },
    {
        id: generateMessageId(),
        from: `${buildJid('red-media', MOCK_CONFERENCE)}/RedA1`,
        to: buildJid('red-media', MOCK_CONFERENCE),
        type: 'groupchat',
        body: 'Monitoring social media channels. No significant activity detected.',
        delay: {
            timestamp: new Date('2025-01-20T09:20:00.000Z'),
        },
    },
    {
        id: generateMessageId(),
        from: `${buildJid('red-media', MOCK_CONFERENCE)}/RedCmd`,
        to: buildJid('red-media', MOCK_CONFERENCE),
        type: 'groupchat',
        body: 'Good. Keep tracking Blue Force information operations.',
        delay: {
            timestamp: new Date('2025-01-20T09:21:00.000Z'),
        },
    },
    // Red Logistics messages
    {
        id: generateMessageId(),
        from: `${buildJid('red-logistics', MOCK_CONFERENCE)}/RedA1`,
        to: buildJid('red-logistics', MOCK_CONFERENCE),
        type: 'groupchat',
        body: 'Supply status: All units at 100% fuel and ammunition.',
        delay: {
            timestamp: new Date('2025-01-20T09:08:00.000Z'),
        },
    },
    {
        id: generateMessageId(),
        from: `${buildJid('red-logistics', MOCK_CONFERENCE)}/RedCmd`,
        to: buildJid('red-logistics', MOCK_CONFERENCE),
        type: 'groupchat',
        body: 'Excellent. Maintain readiness for extended operations.',
        delay: {
            timestamp: new Date('2025-01-20T09:09:00.000Z'),
        },
    },
    {
        id: generateMessageId(),
        from: `${buildJid('red-logistics', MOCK_CONFERENCE)}/RedA1`,
        to: buildJid('red-logistics', MOCK_CONFERENCE),
        type: 'groupchat',
        body: 'Medical supplies confirmed available at all forward positions.',
        delay: {
            timestamp: new Date('2025-01-20T09:25:00.000Z'),
        },
    },
    // Red HQ messages
    {
        id: generateMessageId(),
        from: `${buildJid('red-HQ', MOCK_CONFERENCE)}/RedCmd`,
        to: buildJid('red-HQ', MOCK_CONFERENCE),
        type: 'groupchat',
        body: 'HQ team, I need eyes on all operational channels.',
        delay: {
            timestamp: new Date('2025-01-20T09:03:00.000Z'),
        },
    },
    {
        id: generateMessageId(),
        from: `${buildJid('red-HQ', MOCK_CONFERENCE)}/RedA1`,
        to: buildJid('red-HQ', MOCK_CONFERENCE),
        type: 'groupchat',
        body: 'Monitoring command, media, and logistics channels now.',
        delay: {
            timestamp: new Date('2025-01-20T09:04:00.000Z'),
        },
    },
    {
        id: generateMessageId(),
        from: `${buildJid('red-HQ', MOCK_CONFERENCE)}/RedCmd`,
        to: buildJid('red-HQ', MOCK_CONFERENCE),
        type: 'groupchat',
        body: 'Report any anomalies immediately.',
        delay: {
            timestamp: new Date('2025-01-20T09:12:00.000Z'),
        },
    },
    {
        id: generateMessageId(),
        from: `${buildJid('red-HQ', MOCK_CONFERENCE)}/RedA1`,
        to: buildJid('red-HQ', MOCK_CONFERENCE),
        type: 'groupchat',
        body: 'Understood. All channels nominal so far.',
        delay: {
            timestamp: new Date('2025-01-20T09:13:00.000Z'),
        },
    },
];
/**
 * Convert MOCK fixtures to unified storage format and seed
 */
export async function seedMockWargame(storage) {
    // Convert MOCK_USERS to UnifiedUser format
    const unifiedUsers = MOCK_USERS.map((user) => {
        const baseUser = {
            username: user.jid.split('@')[0] || '',
            jid: user.jid,
            name: user.displayName || user.jid.split('@')[0] || '',
            email: `${user.jid.split('@')[0]}@${MOCK_DOMAIN}`,
            password: user.jid.split('@')[0] || '', // username as password
            groups: user.groups,
            isGameMaster: user.groups.includes('Game Masters'),
            createdAt: '2025-01-15T08:00:00.000Z',
        };
        return baseUser;
    });
    // Seed users
    for (const user of unifiedUsers) {
        await storage.setItem(`entities/users/${user.username}`, user);
    }
    await storage.setItem('entities/users/_index', unifiedUsers.map((u) => u.username));
    // Convert MOCK_FORCES to UnifiedForce format
    const unifiedForces = MOCK_FORCES.map((force) => {
        const baseForce = {
            id: force.id,
            name: force.name,
            color: force.color || '#000000',
            icon: 'military_tech',
            members: force.members.map((jid) => jid.split('@')[0] || ''),
            admins: force.commander ? [force.commander.split('@')[0] || ''] : [],
            objectives: [],
            createdAt: force.createdAt,
            createdBy: force.createdBy.split('@')[0] || '',
        };
        if (force.description) {
            baseForce.description = force.description;
        }
        return baseForce;
    });
    // Seed forces
    for (const force of unifiedForces) {
        await storage.setItem(`entities/forces/${force.id}`, force);
    }
    await storage.setItem('entities/forces/_index', unifiedForces.map((f) => f.id));
    // Convert MOCK_ROOMS to UnifiedRoom format
    const unifiedRooms = MOCK_ROOMS.map((room) => {
        const baseRoom = {
            id: room.jid.split('@')[0] || '',
            jid: room.jid,
            name: room.info.identities?.[0]?.name || '',
            xmpp: {
                persistent: true,
                publicRoom: false,
                membersOnly: false,
                moderated: false,
                maxUsers: 100,
                changeSubject: false,
            },
            wargaming: {
                type: room.extension?.type || 'standard',
            },
            createdAt: room.extension?.createdAt || '2025-01-15T08:00:00.000Z',
            createdBy: room.extension?.createdBy.split('@')[0] || 'gamemaster',
        };
        if (room.extension?.forceRestrictions) {
            baseRoom.wargaming.groupMembers = room.extension.forceRestrictions;
        }
        if (room.extension?.formSchemaIds) {
            baseRoom.wargaming.formTemplates = room.extension.formSchemaIds;
        }
        if (room.extension?.theme) {
            baseRoom.wargaming.theme = room.extension.theme;
        }
        return baseRoom;
    });
    // Seed rooms
    for (const room of unifiedRooms) {
        await storage.setItem(`entities/rooms/${room.id}`, room);
    }
    await storage.setItem('entities/rooms/_index', unifiedRooms.map((r) => r.id));
    // Convert MOCK_FORM_SCHEMAS to UnifiedFormTemplate format
    const unifiedTemplates = MOCK_FORM_SCHEMAS.map((schema) => {
        const baseTemplate = {
            id: schema.id,
            name: schema.title,
            schema: schema.schema,
            category: (schema.tags && schema.tags[0]) || 'general',
            allowedForces: [],
            createdAt: schema.createdAt,
            createdBy: schema.createdBy.split('@')[0] || '',
            version: 1,
        };
        if (schema.description) {
            baseTemplate.description = schema.description;
        }
        if (schema.uiSchema) {
            baseTemplate.uiSchema = schema.uiSchema;
        }
        return baseTemplate;
    });
    // Seed templates
    for (const template of unifiedTemplates) {
        await storage.setItem(`entities/templates/${template.id}`, template);
    }
    await storage.setItem('entities/templates/_index', unifiedTemplates.map((t) => t.id));
    console.info(`✅ Seeded ${unifiedUsers.length} users, ${unifiedForces.length} forces, ${unifiedRooms.length} rooms, ${unifiedTemplates.length} templates`);
}
// ============================================================================
// Form Schemas (RJSF)
// ============================================================================
export const MOCK_FORM_SCHEMAS = [
    {
        id: 'sitrep',
        title: 'Situation Report (SITREP)',
        description: 'Standard situation report format',
        schema: {
            type: 'object',
            required: ['datetime', 'location', 'situation'],
            properties: {
                datetime: {
                    type: 'string',
                    format: 'date-time',
                    title: 'Date/Time',
                },
                location: {
                    type: 'string',
                    title: 'Location',
                },
                situation: {
                    type: 'string',
                    title: 'Situation',
                },
                ownForces: {
                    type: 'string',
                    title: 'Own Forces',
                },
                enemyForces: {
                    type: 'string',
                    title: 'Enemy Forces',
                },
                assessment: {
                    type: 'string',
                    title: 'Assessment',
                },
            },
        },
        uiSchema: {
            datetime: {
                'ui:widget': 'datetime',
            },
            situation: {
                'ui:widget': 'textarea',
                'ui:options': {
                    rows: 5,
                },
            },
            ownForces: {
                'ui:widget': 'textarea',
                'ui:options': {
                    rows: 3,
                },
            },
            enemyForces: {
                'ui:widget': 'textarea',
                'ui:options': {
                    rows: 3,
                },
            },
            assessment: {
                'ui:widget': 'textarea',
                'ui:options': {
                    rows: 3,
                },
            },
        },
        icon: 'description',
        tags: ['report', 'tactical'],
        createdAt: '2025-01-15T08:00:00.000Z',
        createdBy: buildJid('gamemaster', MOCK_DOMAIN),
    },
    {
        id: 'intsum',
        title: 'Intelligence Summary (INTSUM)',
        description: 'Intelligence summary report',
        schema: {
            type: 'object',
            required: ['datetime', 'summary'],
            properties: {
                datetime: {
                    type: 'string',
                    format: 'date-time',
                    title: 'Date/Time',
                },
                summary: {
                    type: 'string',
                    title: 'Summary',
                },
                enemyActivity: {
                    type: 'string',
                    title: 'Enemy Activity',
                },
                intelligence: {
                    type: 'string',
                    title: 'Key Intelligence',
                },
                threats: {
                    type: 'array',
                    title: 'Identified Threats',
                    items: {
                        type: 'string',
                    },
                },
            },
        },
        uiSchema: {
            datetime: {
                'ui:widget': 'datetime',
            },
            summary: {
                'ui:widget': 'textarea',
                'ui:options': {
                    rows: 5,
                },
            },
            enemyActivity: {
                'ui:widget': 'textarea',
                'ui:options': {
                    rows: 3,
                },
            },
            intelligence: {
                'ui:widget': 'textarea',
                'ui:options': {
                    rows: 3,
                },
            },
        },
        icon: 'analytics',
        tags: ['intelligence', 'report'],
        createdAt: '2025-01-15T08:00:00.000Z',
        createdBy: buildJid('gamemaster', MOCK_DOMAIN),
    },
    {
        id: 'oporder',
        title: 'Operations Order (OPORD)',
        description: 'Standard five-paragraph operations order',
        schema: {
            type: 'object',
            required: ['datetime', 'situation', 'mission', 'execution'],
            properties: {
                datetime: {
                    type: 'string',
                    format: 'date-time',
                    title: 'Date/Time',
                },
                situation: {
                    type: 'string',
                    title: '1. Situation',
                },
                mission: {
                    type: 'string',
                    title: '2. Mission',
                },
                execution: {
                    type: 'string',
                    title: '3. Execution',
                },
                sustainment: {
                    type: 'string',
                    title: '4. Sustainment',
                },
                commandAndSignal: {
                    type: 'string',
                    title: '5. Command and Signal',
                },
            },
        },
        uiSchema: {
            datetime: {
                'ui:widget': 'datetime',
            },
            situation: {
                'ui:widget': 'textarea',
                'ui:options': {
                    rows: 4,
                },
            },
            mission: {
                'ui:widget': 'textarea',
                'ui:options': {
                    rows: 3,
                },
            },
            execution: {
                'ui:widget': 'textarea',
                'ui:options': {
                    rows: 5,
                },
            },
            sustainment: {
                'ui:widget': 'textarea',
                'ui:options': {
                    rows: 3,
                },
            },
            commandAndSignal: {
                'ui:widget': 'textarea',
                'ui:options': {
                    rows: 3,
                },
            },
        },
        icon: 'assignment',
        tags: ['order', 'tactical'],
        createdAt: '2025-01-15T08:00:00.000Z',
        createdBy: buildJid('gamemaster', MOCK_DOMAIN),
    },
];
//# sourceMappingURL=fixtures.js.map